import React from 'react';
import { Link } from 'react-router-dom';
import classnames from 'classnames';

import axios from 'axios';

import { forgotPassword, setNlSignup } from '../actions/account';

import Alert from 'react-s-alert';

import auth from '../utils/auth';

import Input from '../components/form/FormikInput.jsx';
import Select from '../components/form/FormikSelect.jsx';
import Checkbox from '../components/form/FormikCheckbox.jsx';

import { useFormik } from 'formik';
import { object, string, boolean } from 'yup';
import AsyncSelect from 'react-select/async';

const randomize = require('randomatic');
const _debounce = require('lodash.debounce');

const register = (userData) => {
    return axios.request({
        url: `/customers`,
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'Session-Key': auth.getSID()
        },
        data: userData
    });
};

const getOrgs = _debounce((searchTerm, callback, values) => {
    let term = searchTerm ? `${searchTerm}~` : 'matchall';
    let url = `/entities/search/${term}`;
    const method = 'GET';
    let params = {
        fields: JSON.stringify(['name', 'entity_id'])
    };

    if (values.org_type) {
        params.category = values.org_type;
    }

    axios
        .request({
            url,
            params,
            method
        })
        .then((resp) => {
            callback(
                resp.data.data.map((v) => ({ label: v.name.long, value: v.entity_id, orig: v })),
                null
            );
        })
        .catch((e) => callback(e, null));
}, 350);

const accountSchema = object().shape({
    name: object().shape({
        given: string().required('Please enter a First Name'),
        family: string().required('Please enter a Last Name')
    }),
    title: string().required('Please enter a Title'),
    email: string().lowercase('Email must be lowercase').strict().email().required('Please enter an Email'),
    phone: string().required('Please enter a Phone Number'),
    org_type: string().required('Please select an Organization Type'),
    nl_signup: boolean().default(true),
    netsuite_entity: object()
        .shape({
            label: string(),
            value: string()
        })
        .required('You must choose a Library/School')
});

const _handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
        let userObject = {
            name: values.name,
            title: values.title,
            email: values.email.toLowerCase(),
            phone: values.phone,
            netsuite_entity: values.netsuite_entity.value,
            password: randomize('*', 16)
        };

        let resp = await register(userObject);
        if (values.nl_signup) {
            setNlSignup(resp.data.customer._id);
        }
        // Kick off "Forgot Password"
        await forgotPassword(userObject.email);

        setSubmitting(false);
        resetForm();
        Alert.success(
            <div>
                <Link to={`/customer/${resp.data.customer._id}`}>User</Link> added.
            </div>
        );
    } catch (e) {
        setSubmitting(false);
        Alert.error(e.response.data.message);
    }
};

const RegisterUser = (props) => {
    const { values, touched, errors, handleChange, handleSubmit, isSubmitting, setFieldValue, setFieldTouched } =
        useFormik({
            initialValues: {
                name: {
                    given: '',
                    family: ''
                },
                title: '',
                email: '',
                phone: '',
                org_type: '',
                netsuite_entity: null,
                nl_signup: false
            },
            validationSchema: accountSchema,
            onSubmit: _handleSubmit
        });
    return (
        <div className="account-create container-fixed">
            <div className="page-header category-header">
                <h1>Create an Account</h1>
            </div>

            <hr />
            <form onSubmit={handleSubmit} noValidate autoComplete="off">
                <div className="fieldset">
                    <div className="row">
                        <div className="col-xs-12 col-md-6">
                            <Input
                                id="name.given"
                                label="First Name"
                                type="text"
                                value={values.name.given}
                                error={errors.name && errors.name.given}
                                touched={touched.name && touched.name.given}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="col-xs-12 col-md-6">
                            <Input
                                id="name.family"
                                label="Last Name"
                                type="text"
                                value={values.name.family}
                                error={errors.name && errors.name.family}
                                touched={touched.name && touched.name.family}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-xs-12 col-md-4">
                            <Input
                                label="Title"
                                type="text"
                                id="title"
                                placeholder="i.e. Branch Manager, Adult Collections Development, etc."
                                value={values.title}
                                error={errors.title}
                                touched={touched.title}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="field col-xs-12 col-md-4">
                            <label htmlFor="email" className="required">
                                Email Address
                            </label>
                            <div className="input-box">
                                <Input
                                    type="email"
                                    id="email"
                                    value={values.email}
                                    error={errors.email}
                                    touched={touched.email}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        <div className="field col-xs-12 col-md-4">
                            <label htmlFor="phone" className="required">
                                Phone
                            </label>
                            <div className="input-box">
                                <Input
                                    type="tel"
                                    id="phone"
                                    placeholder="123-456-7890"
                                    value={values.phone}
                                    error={errors.phone}
                                    touched={touched.phone}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="field col-xs-12 col-md-4">
                            <Select
                                label="Organization Type"
                                id="org_type"
                                value={values.org_type}
                                error={errors.org_type}
                                touched={touched.org_type}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Please select an Organization Type</option>
                                <option value="findaway_direct">Public</option>
                                <option value="military">Military</option>
                                <option value="k-12">K-12</option>
                            </Select>
                        </div>
                        <div className="field col-xs-12 col-md-8">
                            <label className={classnames({ required: !values.no_org })} htmlFor="netsuite_entity">
                                Find Your Library/School
                            </label>
                            <div className="input-box">
                                <AsyncSelect
                                    className={classnames('mb-3', {
                                        'react-select-error': errors.netsuite_entity && touched.netsuite_entity
                                    })}
                                    classNamePrefix="react-select"
                                    id="netsuite_entity"
                                    name="netsuite_entity"
                                    cacheOptions={false}
                                    isClearable
                                    value={values.netsuite_entity}
                                    loadOptions={(searchTerm, callback) => getOrgs(searchTerm, callback, values)}
                                    onChange={(v) => setFieldValue('netsuite_entity', v)}
                                    onBlur={setFieldTouched}
                                    placeholder="Search and Select your Library/School"
                                />
                                {errors.netsuite_entity && touched.netsuite_entity && (
                                    <div className="errors-list" style={{ opacity: 1 }}>
                                        <span>{errors.netsuite_entity}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <Checkbox
                        label="Sign up to receive Playaway emails including special discounts, promotions and our monthly content newsletter."
                        id="nl_signup"
                        checked={values.nl_signup}
                        error={errors.nl_signup}
                        touched={touched.nl_signup}
                        onChange={handleChange}
                    />

                    <div className="txt-center">
                        <button type="submit" className="button button-primary" disabled={isSubmitting}>
                            Submit
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default RegisterUser;
